import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

// Typesense collections a shelf can source from. The music vertical
// (events/venues/artists) plus the cinema/theater verticals
// (movies/screenings/people/plays/performances/show_genres) [MOO-4451].
export const SHELF_COLLECTIONS = [
  "events",
  "venues",
  "artists",
  "movies",
  "screenings",
  "people",
  "plays",
  "performances",
  "show_genres",
] as const;

// Mirrors backend SectionDb (Infrastructure.DataModels.Firestore.Common).
export const SHELF_SECTIONS = ["Music", "Cinema", "Theater"] as const;

const filterSchema = z.object({
  field: z.string(),
  operator: z.enum([
    "eq",
    "neq",
    "in",
    "gt",
    "gte",
    "lt",
    "lte",
    "not in",
    "within",
  ]),
  value: z.string(),
  default: z.string().nullish(),
  // Optional per-filter collection override — backend ShelfFilterDbDto.Collection
  // lets a filter target a different collection than the shelf's primary one.
  collection: z.enum(SHELF_COLLECTIONS).nullish(),
});

const queryRulesSchema = z.object({
  collection: z.enum(SHELF_COLLECTIONS),
  sorting: z
    .enum([
      "Recommended",
      "Popular",
      "Nearby",
      "Following",
      "Date",
      "Random",
      "Trending",
      "RecentlyAdded",
      "Rating",
    ])
    .superRefine(
      fieldConfig({
        fieldType: "sorting",
      }),
    ),
  filters: z.array(filterSchema),
});

export const shelfEditSchema = z.object({
  id: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        inputProps: {
          disabled: true,
        },
      }),
    ),
  slug: z.coerce.string().nullish(),
  name: z.string(),
  queryRules: queryRulesSchema,
  numerator: z.coerce
    .number()
    .default(1)
    .superRefine(
      fieldConfig({
        inputProps: {
          min: 0,
        },
      }),
    ),
  denominator: z.coerce
    .number()
    .default(1)
    .superRefine(
      fieldConfig({
        inputProps: {
          min: 1,
        },
      }),
    ),
  variant: z.enum(["horizontalPills", "verticalPills"]),
  // Which app vertical this shelf belongs to — backend SectionDb [MOO-4451].
  section: z.enum(SHELF_SECTIONS).default("Music"),
  hide: z.boolean().default(false),
  isQuickAction: z.boolean().default(false),
  quickActionIcon: z
    .object({
      name: z.string().nullish(),
      code: z.coerce.number().nullish(),
    })
    .nullish(),
  context: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(
      fieldConfig({
        fieldType: "shelfContext",
      }),
    ),
  createdBy: z
    .string()
    .nullish()
    .superRefine(
      fieldConfig({
        inputProps: {
          disabled: true,
        },
      }),
    ),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "datetime",
        inputProps: {
          disabled: true,
        },
      }),
    ),
});

export type ShelvesEditFormType = z.infer<typeof shelfEditSchema>;
